import React from "react";
import { motion } from "framer-motion";
import {
  ClipboardList,
  LineChart,
  Layers,
  ArrowUpRight,
} from "lucide-react";

const tools = [
  {
    id: "lease",
    name: "LeaseLens v5",
    code: "01",
    icon: ClipboardList,
    desc: "Finance-grade lease accounting for Ind AS 116 / IFRS 16. ROU, liabilities, modifications, journal entries, full disclosure.",
    tags: ["Ind AS 116", "IFRS 16", "Journals", "Modifications"],
    accent: "from-emerald-700 to-emerald-900",
    href: "/hub/"
  },
  {
    id: "esop",
    name: "ESOP Valuation",
    code: "02",
    icon: LineChart,
    desc: "Black-Scholes ESOP & RSU report generator. Multi-tranche, comparables, sensitivity tables and signed-off PDF reports.",
    tags: ["Black-Scholes", "DLOM", "RSU", "PDF"],
    accent: "from-amber-700 to-amber-900",
    href: "/hub/"
  },
  {
    id: "fc",
    name: "FinConsolidate",
    code: "03",
    icon: Layers,
    desc: "Multi-entity consolidation, Excel-native intake, automated validation, real-time audit trail and role-based subsidiary submission.",
    tags: ["Multi-entity", "Excel native", "Audit trail", "RBAC"],
    accent: "from-sky-800 to-sky-950",
    href: "/hub/"
  },
];

export default function Tools() {
  return (
    <section
      id="tools"
      className="relative py-28 md:py-36 px-6 md:px-10 bg-[#fafaf9]"
      data-testid="tools-section"
    >
      <div className="max-w-7xl mx-auto">
        <div className="flex items-end justify-between gap-8 flex-wrap mb-14">
          <div>
            <div className="text-[11px] uppercase tracking-[0.28em] text-gray-500 font-mono-x mb-4">
              §02 · Platform Tools
            </div>
            <h2 className="font-display text-4xl md:text-6xl font-semibold text-[#202A36] leading-[1.02] tracking-[-0.03em] max-w-3xl">
              Everything your finance team needs,
              <span className="text-gray-400"> in one place.</span>
            </h2>
          </div>
          <p className="text-gray-600 max-w-sm leading-relaxed">
            Three purpose-built tools, unified under one workspace and a
            single login. No spreadsheet sprawl. No re-keying.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {tools.map((t, i) => {
            const Icon = t.icon;
            return (
              <motion.a
                key={t.id}
                href={t.href}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{
                  duration: 0.7,
                  delay: i * 0.08,
                  ease: [0.22, 1, 0.36, 1],
                }}
                whileHover={{ y: -4 }}
                className="group relative overflow-hidden rounded-3xl border border-black/10 bg-white p-7 md:p-9 transition-shadow hover:shadow-[0_30px_60px_-30px_rgba(32,42,54,0.35)]"
                data-testid={`tool-card-${t.id}`}
              >
                <div className="flex items-start justify-between mb-8">
                  <div
                    className={`h-12 w-12 rounded-2xl bg-gradient-to-br ${t.accent} text-white flex items-center justify-center shadow-lg`}
                  >
                    <Icon size={20} strokeWidth={1.6} />
                  </div>
                  <div className="text-right">
                    <div className="font-mono-x text-[10px] uppercase tracking-[0.22em] text-gray-400">
                      Tool
                    </div>
                    <div className="font-display text-3xl text-gray-300 leading-none">
                      {t.code}
                    </div>
                  </div>
                </div>

                <h3 className="font-display text-2xl md:text-3xl font-semibold text-[#202A36] tracking-tight mb-3">
                  {t.name}
                </h3>
                <p className="text-gray-600 leading-relaxed mb-6 max-w-md">
                  {t.desc}
                </p>

                <div className="flex flex-wrap gap-2 mb-8">
                  {t.tags.map((tag) => (
                    <span
                      key={tag}
                      className="inline-flex items-center text-[11px] font-medium text-gray-700 bg-gray-100 border border-black/5 px-2.5 py-1 rounded-full"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                <div className="flex items-center justify-between border-t border-black/5 pt-5">
                  <span className="text-sm font-medium text-[#202A36] inline-flex items-center gap-2">
                    Open tool
                    <ArrowUpRight
                      size={16}
                      className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                    />
                  </span>
                  <span className="font-mono-x text-[10px] uppercase tracking-[0.2em] text-gray-400">
                    audivis.in/{t.id}
                  </span>
                </div>

                {/* hover sheen */}
                <div className="pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                  <div className="absolute -inset-px rounded-3xl bg-gradient-to-br from-[#202A36]/5 via-transparent to-transparent" />
                </div>
              </motion.a>
            );
          })}
        </div>
      </div>
    </section>
  );
}
